export class ApiError extends Error {
  constructor(code, message, status = 0) { super(message); this.name = 'ApiError'; this.code = code; this.status = status }
}

/** access·CSRF는 이 클로저에만 존재한다. 쿠키 변경은 탭 간 Web Lock, 갱신은 인스턴스 내 단일 promise로 직렬화한다. */
export function createAuthClient({ area = 'admin', fetchImpl = globalThis.fetch, origin = globalThis.location?.origin,
  now = Date.now, onSession = () => {}, notify = () => {}, protectRequest = async () => null,
  lock = action => {
    if (!globalThis.navigator?.locks) throw new ApiError('BROWSER_UNSUPPORTED', '최신 브라우저에서 다시 접속해 주세요.')
    return globalThis.navigator.locks.request('techedu-browser-auth-cookie', action)
  } } = {}) {
  if (!['admin', 'user'].includes(area)) throw new Error('Invalid authentication area')
  const prefix = `/api/${area}/auth`
  let access = null, expiry = 0, epoch = 0, identity = null, csrfToken = null, csrfFlight = null, refreshFlight = null
  const signature = value => value ? value.memberKey + ':' + [...value.roles].sort().join(',') : ''
  // 사용자 영역은 회원번호 대신 서버가 만든 불투명 memberKey를 준다. 관리자 영역 응답은 mberNo를 쓴다.
  const memberKeyOf = data => typeof data?.memberKey === 'string' ? data.memberKey : data?.mberNo
  const clear = (broadcast = true) => { epoch++; access = null; expiry = 0; identity = null; onSession(null); if (broadcast) notify() }
  const checkEpoch = value => { if (value !== epoch) throw new ApiError('SESSION_CHANGED', '로그인 상태가 변경되었습니다. 다시 로그인해 주세요.', 401) }

  async function fetchEnvelope(path, init, responseType) {
    if (!origin?.startsWith('https://')) throw new ApiError('HTTPS_REQUIRED', 'HTTPS 주소로 접속해 주세요.')
    if (!path.startsWith('/api/') || path.includes('\\') || path.includes('://')) throw new ApiError('INVALID_PATH', '허용되지 않은 요청입니다.')
    const controller = new AbortController(), timeout = setTimeout(() => controller.abort(), 20000)
    try {
      const response = await fetchImpl(path, { ...init, credentials: 'include', cache: 'no-store', signal: controller.signal })
      // 인증 이미지도 동일한 갱신·세션 epoch 경계를 거치고 실패 응답은 JSON 오류로 처리한다.
      if (responseType === 'blob' && response.ok) return { success: true, status: response.status, data: await response.blob() }
      let body
      try { body = await response.json() } catch { throw new ApiError('INVALID_RESPONSE', '서버 응답을 확인할 수 없습니다.', response.status) }
      if (!response.ok || body.success !== true) throw new ApiError(body.code || 'REQUEST_FAILED', body.message || '요청을 처리하지 못했습니다.', response.status)
      return { ...body, status: response.status }
    } catch (error) {
      if (error instanceof ApiError) throw error
      throw new ApiError('NETWORK_ERROR', '서버에 연결하지 못했습니다. 연결 상태를 확인하고 다시 시도해 주세요.')
    } finally { clearTimeout(timeout) }
  }
  async function csrf() {
    if (csrfToken) return csrfToken
    if (!csrfFlight) csrfFlight = fetchEnvelope('/api/common/csrf', { method: 'GET' }).then(result => {
      if (typeof result.data?.token !== 'string') throw new ApiError('INVALID_RESPONSE', '인증 요청을 준비하지 못했습니다.')
      csrfToken = result.data.token; return csrfToken
    }).finally(() => { csrfFlight = null })
    return csrfFlight
  }
  async function raw(path, { method = 'GET', body, token, responseType, learningToken } = {}) {
    const headers = { Accept: 'application/json' }
    if (token) headers.Authorization = `Bearer ${token}`
    if (learningToken) headers['X-Learning-Token'] = learningToken
    // multipart boundary는 브라우저가 생성한다. 업로드도 같은 CSRF·갱신·세션 변경 경계를 거친다.
    const multipart = typeof FormData !== 'undefined' && body instanceof FormData
    if (method !== 'GET') { headers['X-CSRF-TOKEN'] = await csrf(); if (!multipart) headers['Content-Type'] = 'application/json' }
    return fetchEnvelope(path, { method, headers, body: method === 'GET' ? undefined : multipart ? body : JSON.stringify(body ?? {}) }, responseType)
  }
  function accept(data, version) {
    checkEpoch(version)
    if (!data?.accessToken || typeof memberKeyOf(data) !== 'string' || !Array.isArray(data.roles) || !(data.expiresIn > 0)) {
      throw new ApiError('INVALID_RESPONSE', '로그인 응답을 확인할 수 없습니다.')
    }
    const next = { memberKey: memberKeyOf(data), roles: [...data.roles] }
    // 갱신 쿠키의 계정/역할이 달라졌으면 이전 요청의 결과·자동 재전송을 새로운 주체에게 넘기지 않는다.
    if (identity && signature(identity) !== signature(next)) epoch++
    access = data.accessToken; expiry = now() + data.expiresIn * 1000; identity = next
    onSession(next); return next
  }
  function refresh() {
    if (refreshFlight) return refreshFlight
    const version = epoch
    refreshFlight = Promise.resolve().then(() => lock(async () => {
      checkEpoch(version)
      // 다른 탭이 쿠키를 처음 만들었을 수 있으므로 잠금 안에서 새 마스킹 토큰을 받는다.
      csrfToken = null
      return accept((await raw(`${prefix}/refresh`, { method: 'POST' })).data, version)
    })).catch(error => { if (epoch === version) clear(); throw error }).finally(() => { refreshFlight = null })
    return refreshFlight
  }
  async function request(path, options = {}) {
    const version = epoch
    if (!access || now() >= expiry - 10000) await refresh()
    checkEpoch(version)
    const sent = access
    // 권한 검토에는 경로·메서드만 전달한다. 비밀번호/본문/토큰을 검사 연결점에 복제하지 않는다.
    async function guarded(token) {
      const completed = await protectRequest(path, { method: options.method ?? 'GET' })
      checkEpoch(version)
      const result = await raw(path, { ...options, token }); checkEpoch(version)
      if (completed) { await completed(); checkEpoch(version) }
      return result.data
    }
    try { return await guarded(sent) }
    catch (error) {
      if (error.status !== 401 || !['INVALID_TOKEN', 'UNAUTHORIZED'].includes(error.code)) throw error
      checkEpoch(version)
      if (sent === access) await refresh()
      checkEpoch(version)
      try { return await guarded(access) }
      catch (retryError) { if (retryError.status === 401 && ['INVALID_TOKEN', 'UNAUTHORIZED'].includes(retryError.code)) clear(); throw retryError }
    }
  }
  return {
    clear, refresh, request,
    // 비회원 게시판의 명시 GET 경로만 허용한다. 쓰기/임의 파일 API와 토큰은 노출하지 않는다.
    async publicBoardRead(path, responseType) {
      const pathname = path.split('?')[0]
      if (area !== 'user' || !/^\/api\/public\/boards\/(?:by-path|[a-z][a-z0-9-]{1,49}(?:\/posts(?:\/[1-9]\d{0,18}(?:\/comments|\/files\/[1-9]\d{0,18}\/[1-9]\d{0,3})?)?)?)$/.test(pathname)
        || path.includes('#') || path.includes('\\')) throw new ApiError('INVALID_PATH', '허용되지 않은 요청입니다.')
      const version = epoch, result = await raw(path, { responseType })
      checkEpoch(version); return result.data
    },
    // 메일·문자 재설정·비회원 방문의 공개 경로만 허용한다. 업무 요청은 request의 세션 검사를 사용한다.
    async publicRequest(path, body) {
      if (![`${prefix}/password-reset/request`, `${prefix}/password-reset/finish`, `${prefix}/password-reset/sms/request`, `${prefix}/password-reset/sms/finish`, '/api/common/visits'].includes(path)) {
        throw new ApiError('INVALID_PATH', '허용되지 않은 요청입니다.')
      }
      const version = epoch
      return lock(async () => {
        checkEpoch(version)
        const result = await raw(path, { method: 'POST', body })
        checkEpoch(version)
        return result.data
      })
    },
    async login(mberId, password) {
      clear(); const version = epoch
      return lock(async () => {
        checkEpoch(version)
        const result = await raw(`${prefix}/login`, { method: 'POST', body: { mberId, password } })
        checkEpoch(version)
        if (result.status !== 200) throw new ApiError('INVALID_RESPONSE', '로그인 응답을 확인해 주세요.')
        const identity = accept(result.data, version); notify(); return identity
      })
    },
    async logout() {
      clear()
      // 진행 중인 refresh 응답 뒤 현재 쿠키를 폐기한다. 늦은 응답이 메모리 세션을 되살리지 못한다.
      await lock(() => raw(`${prefix}/logout`, { method: 'POST' }))
    },
  }
}
